/**
 * Lookbook flipbook viewer.
 *
 * Each lookbook is shown from one of two sources:
 *   - page images uploaded to the metaobject (fastest, served by Shopify's CDN)
 *   - otherwise the compressed PDF, drawn page by page with PDF.js
 *
 * Source pages saved as two-page spreads (about twice as wide as the single
 * pages) are split into a left and a right book page.
 *
 * page-flip (~10 KB gz) and PDF.js (~450 KB gz, PDF books only) are fetched on
 * first open, or when the shopper hovers/focuses a "View" button, so the grid
 * page itself ships only this file.
 */
(() => {
  const DPR = Math.min(window.devicePixelRatio || 1, 2);
  const MAX_PAGE_PX = 1800; // widest bitmap drawn for a book page
  const MAX_ZOOM_AREA = 12e6; // stay under iOS canvas limits (~16.7M px)
  const KEEP_RADIUS = 8; // PDF pages further than this from view are released
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let ctx = null; // { root, books, dialog, ui }
  let view = null; // the open book
  let openToken = 0;
  let lastTrigger = null;

  /* ---------------------------------------------------------------- libs */

  const absolute = (url) => new URL(url, window.location.href).href;

  let flipPromise = null;
  function loadFlip() {
    if (window.St && window.St.PageFlip) return Promise.resolve(window.St);
    if (!flipPromise) {
      flipPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = ctx.root.dataset.flipSrc;
        script.async = true;
        script.onload = () => resolve(window.St);
        script.onerror = () => {
          flipPromise = null;
          reject(new Error('page-flip failed to load'));
        };
        document.head.appendChild(script);
      });
    }
    return flipPromise;
  }

  let pdfPromise = null;
  function loadPdf() {
    if (!pdfPromise) {
      pdfPromise = import(absolute(ctx.root.dataset.pdfSrc))
        .then((lib) => {
          lib.GlobalWorkerOptions.workerSrc = absolute(ctx.root.dataset.pdfWorkerSrc);
          return lib;
        })
        .catch((err) => {
          pdfPromise = null;
          throw err;
        });
    }
    return pdfPromise;
  }

  const usesImages = (book) => Array.isArray(book.pages) && book.pages.length > 0;

  function prefetch(event) {
    const trigger = event.target.closest && event.target.closest('[data-lookbook-open]');
    if (!trigger || trigger.dataset.prefetched) return;
    trigger.dataset.prefetched = '1';
    const book = ctx.books[trigger.dataset.lookbookOpen];
    loadFlip().catch(() => {});
    if (book && !usesImages(book) && book.pdf) loadPdf().catch(() => {});
  }

  /* ------------------------------------------------------------- history */

  const hashFor = (handle, page) => `#lookbook=${encodeURIComponent(handle)}&page=${page}`;

  function parseHash() {
    const match = window.location.hash.match(/^#lookbook=([^&]+)(?:&page=(\d+))?/);
    if (!match || !ctx) return null;
    const handle = decodeURIComponent(match[1]);
    if (!ctx.books[handle]) return null;
    return { handle, page: Math.max(1, parseInt(match[2] || '1', 10)) };
  }

  const ownsHistoryEntry = () => !!(history.state && history.state.lookbook);

  function requestClose() {
    if (ownsHistoryEntry()) {
      history.back(); // popstate closes the viewer
    } else {
      closeViewer();
      history.replaceState(history.state, '', window.location.pathname + window.location.search);
    }
  }

  /* ---------------------------------------------------------- open/close */

  const alive = (v) => v === view && v.token === openToken;

  async function openBook(handle, page = 1, { pushHistory = true } = {}) {
    const book = ctx.books[handle];
    if (!book || (!usesImages(book) && !book.pdf)) return;

    teardown();
    const v = {
      handle,
      book,
      token: ++openToken,
      images: usesImages(book),
      count: 0,
      ratio: 0.75,
      pages: [], // book page -> { src: source page index, half: null | 'left' | 'right' }
      spreads: new Set(), // source pages that are split in two
      start: page - 1,
      flip: null,
      bookEl: null,
      pageEls: [],
      pdfTask: null,
      pdf: null,
      pdfPages: new Map(), // book page -> { url, width }
      jobs: new Map(),
      busy: false,
      objectUrls: new Set(),
      zoomLevel: 1,
      thumbsBuilt: false,
      thumbsQueued: new Set(),
      thumbObserver: null,
    };
    view = v;

    if (pushHistory) history.pushState({ lookbook: handle }, '', hashFor(handle, page));

    setupChrome(book);
    showStatus('loading');
    if (!ctx.dialog.open) ctx.dialog.showModal();
    ctx.dialog.focus({ preventScroll: true });
    document.documentElement.classList.add('lb-scroll-lock');

    try {
      const [St] = await Promise.all([loadFlip(), v.images ? prepareImages(v) : preparePdf(v)]);
      if (!alive(v)) return;
      buildBook(St, v);
    } catch (err) {
      if (!alive(v)) return;
      console.error('[lookbook]', err);
      showStatus('error');
    }
  }

  /**
   * Map source pages to book pages. The narrowest source page sets the page
   * shape; anything at least 1.6x wider is a spread and becomes two pages.
   */
  function layoutPages(v, ratios) {
    const known = ratios.filter((r) => r > 0);
    const single = known.length ? Math.min(...known) : 0.75;
    v.ratio = single;
    v.pages = [];
    v.spreads.clear();
    ratios.forEach((ratio, src) => {
      if (ratio >= single * 1.6) {
        v.spreads.add(src);
        v.pages.push({ src, half: 'left' }, { src, half: 'right' });
      } else {
        v.pages.push({ src, half: null });
      }
    });
    v.count = v.pages.length;
  }

  async function prepareImages(v) {
    let ratios = v.book.pages.map((page) => Number(page.ratio) || 0);
    if (ratios.some((r) => !r)) {
      // No ratio in the data: measure the first page and assume the rest match.
      const probe = new Image();
      probe.src = v.book.pages[0].thumb;
      let fallback = 0.75;
      try {
        await probe.decode();
        if (probe.naturalWidth) fallback = probe.naturalWidth / probe.naturalHeight;
      } catch (e) {
        /* keep the default ratio */
      }
      ratios = ratios.map((r) => r || fallback);
    }
    layoutPages(v, ratios);
  }

  async function preparePdf(v) {
    const lib = await loadPdf();
    if (!alive(v)) return;
    // Range requests: with disableAutoFetch + disableStream, PDF.js downloads
    // only the bytes for the pages actually drawn.
    v.pdfTask = lib.getDocument({
      url: absolute(v.book.pdf),
      disableAutoFetch: true,
      disableStream: true,
      isEvalSupported: false,
    });
    v.pdf = await v.pdfTask.promise;
    // Page sizes only need each page's dictionary, not its images.
    const ratios = await Promise.all(
      Array.from({ length: v.pdf.numPages }, (_, i) =>
        v.pdf.getPage(i + 1).then((page) => {
          const viewport = page.getViewport({ scale: 1 });
          return viewport.width / viewport.height;
        })
      )
    );
    layoutPages(v, ratios);
  }

  function buildBook(St, v) {
    for (let i = 0; i < v.count; i++) {
      const pageEl = document.createElement('div');
      pageEl.className = 'lb-page';
      const img = document.createElement('img');
      img.className = 'lb-page__img';
      img.alt = `Page ${i + 1}`;
      img.decoding = 'async';
      img.draggable = false;
      pageEl.appendChild(img);
      v.pageEls.push(pageEl);
    }

    v.bookEl = document.createElement('div');
    v.bookEl.className = 'lb-book';
    ctx.ui.stage.appendChild(v.bookEl);

    const height = 1000;
    v.flip = new St.PageFlip(v.bookEl, {
      width: Math.round(height * v.ratio),
      height,
      size: 'stretch',
      minWidth: 280, // the stage switches to single pages below 2x this width
      maxWidth: 2000,
      minHeight: 200,
      maxHeight: 2600,
      autoSize: false, // .lb-book is sized by CSS to fill the stage
      showCover: true,
      usePortrait: true,
      mobileScrollSupport: false,
      maxShadowOpacity: 0.45,
      flippingTime: reducedMotion ? 300 : 800,
      startPage: Math.min(Math.max(v.start, 0), v.count - 1),
    });

    v.flip.on('init', () => {
      if (!alive(v)) return;
      // page-flip lays out a spread before switching to single pages on narrow
      // screens, which can leave the left page of the spread showing.
      const start = Math.min(Math.max(v.start, 0), v.count - 1);
      if (!visiblePages(v).includes(start)) v.flip.turnToPage(start);
      v.bookEl.classList.add('is-ready');
      ctx.dialog.dataset.state = 'ready';
      hideStatus();
      onPageChange(v);
    });
    v.flip.on('flip', () => alive(v) && onPageChange(v));
    v.flip.on('changeOrientation', () => alive(v) && onPageChange(v));

    v.flip.loadFromHTML(v.pageEls);

    // page-flip turns the covers into stiff "hard" boards. Their 3D rotation
    // lifts off the spine and drags a grey shadow over the empty side, so
    // let every page curl like paper.
    for (let i = 0; i < v.count; i++) v.flip.getPage(i).setDensity('soft');
  }

  function teardown() {
    openToken++;
    const v = view;
    view = null;
    if (!v) return;
    if (v.thumbObserver) v.thumbObserver.disconnect();
    if (v.flip) {
      try {
        v.flip.destroy();
      } catch (e) {
        /* page-flip was not fully initialised */
      }
    }
    if (v.bookEl) v.bookEl.remove();
    if (v.pdfTask) v.pdfTask.destroy();
    v.objectUrls.forEach((url) => URL.revokeObjectURL(url));
    v.objectUrls.clear();

    const { ui } = ctx;
    ui.zoom.hidden = true;
    ui.zoom.textContent = '';
    ui.thumbs.hidden = true;
    ui.thumbsGrid.textContent = '';
    ui.thumbsToggle.setAttribute('aria-pressed', 'false');
    ui.counter.textContent = '';
  }

  function closeViewer() {
    teardown();
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    if (ctx.dialog.open) ctx.dialog.close();
    document.documentElement.classList.remove('lb-scroll-lock');
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus({ preventScroll: true });
  }

  /* ------------------------------------------------------------- chrome */

  function setupChrome(book) {
    const { ui } = ctx;
    ui.download.hidden = !book.download;
    if (book.download) ui.download.href = book.download;
    ui.collection.hidden = !book.collection;
    if (book.collection) ui.collection.href = book.collection;
    ui.fullscreen.hidden = !document.fullscreenEnabled;
    ui.zoomIn.disabled = false;
    ui.zoomOut.disabled = true;
    ui.prev.disabled = true;
    ui.next.disabled = true;
    ctx.dialog.setAttribute('aria-label', `${book.title} lookbook`);
  }

  function showStatus(kind) {
    const { status } = ctx.ui;
    ctx.dialog.dataset.state = kind;
    status.hidden = false;
    status.textContent = '';
    if (kind === 'loading') {
      status.innerHTML =
        '<span class="lb-spinner" aria-hidden="true"></span><span class="visually-hidden">Loading lookbook</span>';
      return;
    }
    const message = document.createElement('p');
    message.textContent = "Sorry, this lookbook couldn't be opened.";
    status.appendChild(message);
    if (view && view.book.download) {
      const link = document.createElement('a');
      link.href = view.book.download;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = 'Download the PDF instead';
      status.appendChild(link);
    }
  }

  function hideStatus() {
    ctx.ui.status.hidden = true;
  }

  let toastTimer = null;
  function toast(text) {
    const el = ctx.ui.toast;
    el.textContent = text;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (el.hidden = true), 2200);
  }

  /* ------------------------------------------------------------ paging */

  const isPortrait = (v) => v.flip.getOrientation() === 'portrait';

  function visiblePages(v) {
    const index = v.flip.getCurrentPageIndex();
    if (isPortrait(v)) return [index];
    if (index === 0) return [0]; // cover sits alone
    const left = index % 2 === 1 ? index : index - 1;
    return left + 1 < v.count ? [left, left + 1] : [left];
  }

  const pageCssWidth = (v) => {
    const rect = v.flip && v.flip.getBoundsRect();
    return rect && rect.pageWidth ? rect.pageWidth : ctx.ui.stage.clientWidth / 2;
  };

  function onPageChange(v) {
    const { ui } = ctx;
    const visible = visiblePages(v);
    const first = visible[0];
    const last = visible[visible.length - 1];

    v.bookEl.classList.toggle('is-portrait', isPortrait(v));
    ui.counter.textContent =
      visible.length === 2 ? `${first + 1}–${last + 1} / ${v.count}` : `${first + 1} / ${v.count}`;
    ui.prev.disabled = first <= 0;
    ui.next.disabled = last >= v.count - 1;

    history.replaceState(history.state, '', hashFor(v.handle, first + 1));
    loadAround(v, first, last);

    if (v.thumbsBuilt) {
      ui.thumbsGrid.querySelectorAll('.lb-thumb').forEach((thumb, i) => {
        const current = i >= first && i <= last;
        thumb.classList.toggle('is-current', current);
        if (current) thumb.setAttribute('aria-current', 'page');
        else thumb.removeAttribute('aria-current');
      });
    }
  }

  function goTo(v, index) {
    closeThumbs();
    v.flip.turnToPage(index);
    onPageChange(v);
  }

  /** Load the visible pages first, then two before and three after. */
  function loadAround(v, first, last) {
    const from = Math.max(0, first - 2);
    const to = Math.min(v.count - 1, last + 3);
    const distance = (i) => (i < first ? first - i : i > last ? i - last : 0);

    if (v.images) {
      const width = Math.ceil(pageCssWidth(v));
      for (let i = from; i <= to; i++) loadImagePage(v, i, width);
      return;
    }

    // One render per source page; a spread fills both of its book pages.
    const wanted = new Map(); // source page -> priority
    for (let i = from; i <= to; i++) {
      const { src } = v.pages[i];
      wanted.set(src, Math.min(wanted.has(src) ? wanted.get(src) : Infinity, 1 + distance(i)));
    }
    // Drop queued renders that are no longer near the reader.
    for (const key of v.jobs.keys()) {
      if (key.startsWith('page:') && !wanted.has(Number(key.slice(5)))) v.jobs.delete(key);
    }
    wanted.forEach((priority, src) => queuePdfSource(v, src, priority));

    // Release bitmaps far away so long books don't exhaust phone memory.
    for (const [i, record] of v.pdfPages) {
      if (distance(i) > KEEP_RADIUS) {
        const img = v.pageEls[i].firstChild;
        img.removeAttribute('src');
        img.classList.remove('is-loaded');
        revoke(v, record.url);
        v.pdfPages.delete(i);
      }
    }
  }

  function loadImagePage(v, i, width) {
    const { src, half } = v.pages[i];
    const img = v.pageEls[i].firstChild;
    img.sizes = `${half ? width * 2 : width}px`;
    if (img.dataset.loaded) return;
    img.dataset.loaded = '1';
    if (half) img.dataset.half = half;
    const page = v.book.pages[src];
    img.onload = () => img.classList.add('is-loaded');
    img.srcset = page.srcset;
    img.src = page.src;
  }

  /* ------------------------------------------------------- PDF drawing */

  function enqueue(v, key, priority, run) {
    const existing = v.jobs.get(key);
    if (existing) {
      existing.priority = priority;
      return;
    }
    v.jobs.set(key, { priority, run });
    pump(v);
  }

  async function pump(v) {
    if (v.busy) return;
    v.busy = true;
    while (alive(v) && v.jobs.size) {
      let bestKey = null;
      let best = null;
      for (const [key, job] of v.jobs) {
        if (!best || job.priority < best.priority) {
          best = job;
          bestKey = key;
        }
      }
      v.jobs.delete(bestKey);
      try {
        await best.run();
      } catch (err) {
        if (alive(v)) console.warn('[lookbook] page render failed', err);
      }
    }
    v.busy = false;
  }

  function revoke(v, url) {
    URL.revokeObjectURL(url);
    v.objectUrls.delete(url);
  }

  /** Encode a canvas as a JPEG blob URL and free the canvas. */
  async function canvasToUrl(v, canvas) {
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.88));
    canvas.width = 0;
    canvas.height = 0;
    if (!blob || !alive(v)) return null;
    const url = URL.createObjectURL(blob);
    v.objectUrls.add(url);
    return url;
  }

  function cropHalf(canvas, side) {
    const width = Math.floor(canvas.width / 2);
    const out = document.createElement('canvas');
    out.width = width;
    out.height = canvas.height;
    const x = side === 'right' ? canvas.width - width : 0;
    out.getContext('2d').drawImage(canvas, x, 0, width, canvas.height, 0, 0, width, canvas.height);
    return out;
  }

  /**
   * Draw one PDF source page so each book page is `width` px wide. Returns
   * blob URLs: [page] for a single page, [left, right] for a spread.
   */
  async function renderPdfPage(v, src, width) {
    const split = v.spreads.has(src);
    const page = await v.pdf.getPage(src + 1);
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: (split ? width * 2 : width) / base.width });
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const canvasContext = canvas.getContext('2d', { alpha: false });
    await page.render({ canvas, canvasContext, viewport }).promise;
    page.cleanup();

    const parts = split ? [cropHalf(canvas, 'left'), cropHalf(canvas, 'right')] : [canvas];
    if (split) {
      canvas.width = 0;
      canvas.height = 0;
    }
    const urls = [];
    for (const part of parts) {
      const url = await canvasToUrl(v, part);
      if (!url) {
        urls.forEach((u) => revoke(v, u));
        return null;
      }
      urls.push(url);
    }
    return urls;
  }

  const urlForHalf = (urls, half) => (half === 'right' ? urls[1] : urls[0]);

  const pdfPageWidth = (v) => Math.min(Math.ceil(pageCssWidth(v) * DPR), MAX_PAGE_PX);

  function queuePdfSource(v, src, priority) {
    const width = pdfPageWidth(v);
    const indexes = [];
    v.pages.forEach((p, i) => p.src === src && indexes.push(i));
    const sharpEnough = indexes.every((i) => {
      const have = v.pdfPages.get(i);
      return have && have.width >= width * 0.8;
    });
    if (sharpEnough) return;
    enqueue(v, `page:${src}`, priority, async () => {
      const urls = await renderPdfPage(v, src, width);
      if (!urls) return;
      indexes.forEach((i) => {
        const url = urlForHalf(urls, v.pages[i].half);
        const img = v.pageEls[i].firstChild;
        const previous = v.pdfPages.get(i);
        v.pdfPages.set(i, { url, width });
        img.onload = () => img.classList.add('is-loaded');
        img.src = url;
        if (previous) revoke(v, previous.url);
      });
    });
  }

  /* ---------------------------------------------------------- thumbnails */

  function buildThumbs(v) {
    const { thumbsGrid, thumbs } = ctx.ui;
    thumbsGrid.textContent = '';
    for (let i = 0; i < v.count; i++) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'lb-thumb';
      button.dataset.index = i;
      button.dataset.src = v.pages[i].src;
      button.setAttribute('aria-label', `Go to page ${i + 1}`);
      const frame = document.createElement('span');
      frame.className = 'lb-thumb__frame';
      frame.style.aspectRatio = String(v.ratio);
      const img = document.createElement('img');
      img.alt = '';
      img.decoding = 'async';
      if (v.images) {
        img.loading = 'lazy';
        img.src = v.book.pages[v.pages[i].src].thumb;
        if (v.pages[i].half) img.dataset.half = v.pages[i].half;
      }
      frame.appendChild(img);
      const label = document.createElement('span');
      label.textContent = i + 1;
      button.append(frame, label);
      thumbsGrid.appendChild(button);
    }

    if (!v.images) {
      const width = Math.min(Math.ceil(150 * DPR), 300);
      v.thumbObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            v.thumbObserver.unobserve(entry.target);
            const src = Number(entry.target.dataset.src);
            if (v.thumbsQueued.has(src)) return;
            v.thumbsQueued.add(src);
            enqueue(v, `thumb:${src}`, 50 + src, async () => {
              const urls = await renderPdfPage(v, src, width);
              if (!urls) return;
              thumbsGrid.querySelectorAll(`.lb-thumb[data-src="${src}"]`).forEach((thumb) => {
                const index = Number(thumb.dataset.index);
                thumb.querySelector('img').src = urlForHalf(urls, v.pages[index].half);
              });
            });
          });
        },
        { root: thumbs, rootMargin: '200px 0px' }
      );
      thumbsGrid.querySelectorAll('.lb-thumb').forEach((el) => v.thumbObserver.observe(el));
    }
    v.thumbsBuilt = true;
  }

  function toggleThumbs() {
    const v = view;
    if (!v || !v.flip) return;
    const { ui } = ctx;
    if (!ui.thumbs.hidden) return closeThumbs();
    setZoom(v, 1);
    if (!v.thumbsBuilt) buildThumbs(v);
    ui.thumbs.hidden = false;
    ui.thumbsToggle.setAttribute('aria-pressed', 'true');
    onPageChange(v);
    const current = ui.thumbsGrid.querySelector('.is-current');
    if (current) current.scrollIntoView({ block: 'center' });
  }

  function closeThumbs() {
    ctx.ui.thumbs.hidden = true;
    ctx.ui.thumbsToggle.setAttribute('aria-pressed', 'false');
  }

  /* ---------------------------------------------------------------- zoom */

  function setZoom(v, level) {
    const { ui } = ctx;
    level = Math.max(1, Math.min(3, level));
    v.zoomLevel = level;
    ui.zoomIn.disabled = level === 3;
    ui.zoomOut.disabled = level === 1;
    if (level === 1) {
      ui.zoom.hidden = true;
      ui.zoom.textContent = '';
      v.jobs.forEach((job, key) => key.startsWith('zoom:') && v.jobs.delete(key));
      return;
    }
    closeThumbs();
    renderZoom(v);
  }

  function renderZoom(v) {
    const { zoom } = ctx.ui;
    const wasOpen = !zoom.hidden && zoom.scrollWidth > 0;
    const centerX = wasOpen ? (zoom.scrollLeft + zoom.clientWidth / 2) / zoom.scrollWidth : 0.5;
    const centerY = wasOpen ? (zoom.scrollTop + zoom.clientHeight / 2) / zoom.scrollHeight : 0.5;

    const pageWidth = pageCssWidth(v) * v.zoomLevel;
    const pageHeight = pageWidth / v.ratio;
    const spread = document.createElement('div');
    spread.className = 'lb-zoom__spread';

    visiblePages(v).forEach((i) => {
      const { src, half } = v.pages[i];
      const frame = document.createElement('div');
      frame.className = 'lb-zoom__page';
      frame.style.width = `${pageWidth}px`;
      frame.style.height = `${pageHeight}px`;
      const img = document.createElement('img');
      img.alt = `Page ${i + 1}, zoomed`;
      img.draggable = false;
      // Show the already-loaded page instantly, then swap in a sharper one.
      const bookImg = v.pageEls[i].firstChild;
      if (bookImg.currentSrc || bookImg.src) img.src = bookImg.currentSrc || bookImg.src;
      frame.appendChild(img);
      spread.appendChild(frame);

      if (v.images) {
        if (half) img.dataset.half = half;
        const sharp = new Image();
        sharp.onload = () => (img.src = sharp.src);
        sharp.src = v.book.pages[src].zoom;
      } else {
        const split = v.spreads.has(src);
        let width = Math.ceil(pageWidth * DPR);
        // Cap the full canvas (both halves of a spread) under the iOS limit.
        width = Math.min(width, Math.floor(Math.sqrt((MAX_ZOOM_AREA * v.ratio) / (split ? 2 : 1))));
        enqueue(v, `zoom:${i}:${v.zoomLevel}`, -1, async () => {
          const urls = await renderPdfPage(v, src, width);
          if (!urls) return;
          const url = urlForHalf(urls, half);
          urls.forEach((u) => u !== url && revoke(v, u));
          if (img.isConnected) img.src = url;
          else revoke(v, url);
        });
      }
    });

    zoom.textContent = '';
    zoom.appendChild(spread);
    zoom.hidden = false;
    zoom.scrollLeft = centerX * zoom.scrollWidth - zoom.clientWidth / 2;
    zoom.scrollTop = centerY * zoom.scrollHeight - zoom.clientHeight / 2;
  }

  function bindZoomPan(zoom) {
    let drag = null;
    zoom.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      drag = { x: e.clientX, y: e.clientY, left: zoom.scrollLeft, top: zoom.scrollTop };
      zoom.classList.add('is-dragging');
      zoom.setPointerCapture(e.pointerId);
    });
    zoom.addEventListener('pointermove', (e) => {
      if (!drag) return;
      zoom.scrollLeft = drag.left - (e.clientX - drag.x);
      zoom.scrollTop = drag.top - (e.clientY - drag.y);
    });
    const end = () => {
      drag = null;
      zoom.classList.remove('is-dragging');
    };
    zoom.addEventListener('pointerup', end);
    zoom.addEventListener('pointercancel', end);
  }

  /* --------------------------------------------------------------- share */

  async function share(v) {
    const page = visiblePages(v)[0] + 1;
    const url = `${window.location.origin}${window.location.pathname}${window.location.search}${hashFor(v.handle, page)}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: v.book.title, url });
        return;
      } catch (e) {
        if (e.name === 'AbortError') return; // shopper closed the share sheet
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast('Link copied');
    } catch (e) {
      toast(url);
    }
  }

  function toggleFullscreen() {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else ctx.dialog.requestFullscreen().catch(() => {});
  }

  /* -------------------------------------------------------------- filter */

  function initFilter(root) {
    const buttons = [...root.querySelectorAll('[data-lookbook-filter]')];
    if (!buttons.length) return;
    const cards = [...root.querySelectorAll('.lookbook-card')];
    const empty = root.querySelector('[data-lookbook-empty]');

    const apply = (key, updateUrl) => {
      if (key && !buttons.some((b) => b.dataset.lookbookFilter === key)) key = '';
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.lookbookFilter === key)));
      let shown = 0;
      cards.forEach((card) => {
        const match = !key || (card.dataset.categories || '').split(/\s+/).includes(key);
        card.hidden = !match;
        if (match) shown++;
      });
      if (empty) empty.hidden = shown > 0;
      if (updateUrl) {
        const url = new URL(window.location.href);
        if (key) url.searchParams.set('category', key);
        else url.searchParams.delete('category');
        history.replaceState(history.state, '', url);
      }
    };

    buttons.forEach((button) =>
      button.addEventListener('click', () => apply(button.dataset.lookbookFilter, true))
    );
    const initial = new URLSearchParams(window.location.search).get('category');
    if (initial) apply(initial, false);
  }

  /* ---------------------------------------------------------------- init */

  function init(root) {
    let books = {};
    try {
      books = JSON.parse(root.querySelector('[data-lookbook-data]').textContent);
    } catch (e) {
      console.error('[lookbook] could not read lookbook data', e);
      return;
    }
    const dialog = root.querySelector('[data-lookbook-dialog]');
    const q = (selector) => dialog.querySelector(selector);
    const ui = {
      stage: q('[data-lb-stage]'),
      status: q('[data-lb-status]'),
      counter: q('[data-lb-counter]'),
      prev: q('[data-lb-prev]'),
      next: q('[data-lb-next]'),
      close: q('[data-lb-close]'),
      thumbsToggle: q('[data-lb-thumbs-toggle]'),
      thumbs: q('[data-lb-thumbs]'),
      thumbsGrid: q('[data-lb-thumbs-grid]'),
      zoom: q('[data-lb-zoom]'),
      zoomIn: q('[data-lb-zoom-in]'),
      zoomOut: q('[data-lb-zoom-out]'),
      fullscreen: q('[data-lb-fullscreen]'),
      share: q('[data-lb-share]'),
      download: q('[data-lb-download]'),
      collection: q('[data-lb-collection]'),
      toast: q('[data-lb-toast]'),
    };
    ctx = { root, books, dialog, ui };
    initFilter(root);

    root.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-lookbook-open]');
      if (!trigger) return;
      lastTrigger = trigger;
      openBook(trigger.dataset.lookbookOpen, 1);
    });
    root.addEventListener('pointerover', prefetch, { passive: true });
    root.addEventListener('focusin', prefetch);

    ui.close.addEventListener('click', requestClose);
    ui.prev.addEventListener('click', () => view && view.flip && view.flip.flipPrev());
    ui.next.addEventListener('click', () => view && view.flip && view.flip.flipNext());
    ui.thumbsToggle.addEventListener('click', toggleThumbs);
    ui.thumbsGrid.addEventListener('click', (e) => {
      const thumb = e.target.closest('.lb-thumb');
      if (thumb && view && view.flip) goTo(view, Number(thumb.dataset.index));
    });
    ui.zoomIn.addEventListener('click', () => view && view.flip && setZoom(view, view.zoomLevel + 1));
    ui.zoomOut.addEventListener('click', () => view && view.flip && setZoom(view, view.zoomLevel - 1));
    ui.fullscreen.addEventListener('click', toggleFullscreen);
    ui.share.addEventListener('click', () => view && view.flip && share(view));
    bindZoomPan(ui.zoom);

    // Esc: back out of thumbnails/zoom first, then close.
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      if (!ui.thumbs.hidden) closeThumbs();
      else if (view && view.zoomLevel > 1) setZoom(view, 1);
      else requestClose();
    });
    // The browser can force-close a dialog; keep our state and history in step.
    dialog.addEventListener('close', () => {
      if (!view) return;
      closeViewer();
      if (ownsHistoryEntry()) history.back();
    });
    dialog.addEventListener('keydown', (e) => {
      if (!view || !view.flip || e.altKey || e.ctrlKey || e.metaKey) return;
      if (!ui.thumbs.hidden || view.zoomLevel > 1) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        view.flip.flipNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        view.flip.flipPrev();
      }
    });

    const target = parseHash();
    if (target) openBook(target.handle, target.page, { pushHistory: false });
  }

  window.addEventListener('popstate', () => {
    if (!ctx) return;
    const target = parseHash();
    if (target && (!view || view.handle !== target.handle)) {
      openBook(target.handle, target.page, { pushHistory: false });
    } else if (!target && ctx.dialog.open) {
      closeViewer();
    }
  });

  let resizeTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      const v = view;
      if (!v || !v.flip) return;
      onPageChange(v); // re-requests sharper pages if the book grew
      if (v.zoomLevel > 1) renderZoom(v);
    }, 250);
  });

  const start = () => {
    const root = document.querySelector('[data-lookbooks]');
    if (root) init(root);
  };
  start();

  // Theme editor re-renders the section in place.
  document.addEventListener('shopify:section:load', (e) => {
    if (!e.target.querySelector('[data-lookbooks]')) return;
    if (ctx) closeViewer();
    start();
  });
})();
